import { useRef, useState } from 'react';
import { ApiError } from '../api/client.ts';
import { formatDate } from '../dates.ts';
import {
  useWhereaboutsReports, useRecordWhereaboutsReport, type WhereaboutsReport,
} from '../api/whereaboutsReports.ts';

const field =
  'mt-1 w-full min-h-[48px] rounded-lg border border-neutral-300 bg-white px-3 ' +
  'text-base text-neutral-900';
const label = 'block text-sm font-semibold text-neutral-700';
const secondary =
  'min-h-[44px] rounded-lg border border-neutral-300 px-3 text-sm font-semibold ' +
  'text-neutral-800 hover:bg-neutral-50 active:bg-neutral-100';
const primary =
  'min-h-[44px] rounded-lg bg-neutral-900 px-4 text-sm font-semibold text-white disabled:bg-neutral-200 disabled:text-neutral-600 disabled:cursor-not-allowed';

/**
 * Where a client is reported to be when the agency does not know where (093,
 * Arthur, 2 October). "Whereabouts unknown" alone throws away what somebody
 * took the trouble to learn; this says "reported in Florida, last known at
 * their apartment, visited March 2025".
 *
 * The shape it exists for, and which will recur: a PROPERTY-ONLY guardianship,
 * a facility under no duty to tell the agency anything, the client leaves, and
 * the agency finds out by turning up. Their last placement ends as "Left
 * without notice".
 *
 * Reports are APPEND-ONLY: the latest is current while the whereabouts are
 * unknown, and every earlier one is kept and listed. How the agency learned is
 * a fact about the client; it is shown to whoever may see the client, as every
 * client field is.
 */
export function WhereaboutsReports({ clientId, mayRecord }: { clientId: string; mayRecord: boolean }) {
  const q = useWhereaboutsReports(clientId);
  const [adding, setAdding] = useState(false);
  if (!q.data) return null;
  const { whereabouts_unknown: unknown, reports, last_known: last, last_end_reason: ended } = q.data;
  if (!unknown && reports.length === 0) return null;

  const [latest, ...earlier] = reports;
  const current = unknown ? latest : undefined;
  const history = unknown ? earlier : reports;

  return (
    <div className="mt-4 border-t border-neutral-200 pt-3">
      <h3 className="text-xs font-bold tracking-[0.12em] text-neutral-500 uppercase">
        {unknown ? 'Whereabouts unknown' : 'Earlier reports of where they were'}
      </h3>

      {unknown && (current ? <Report r={current} lead="Reported in" /> : (
        <p className="mt-1 text-sm text-neutral-600">No report of where they are.</p>
      ))}

      {unknown && last && (
        <p className="mt-1 text-sm text-neutral-700">
          Last known: <span className="font-medium">{last.place}</span>, visited {formatDate(last.visit_date)}
          {last.approximate && ' (place approximate)'}
          {ended && <> · {ended.toLowerCase()}</>}
        </p>
      )}

      {history.length > 0 && (
        <details className="mt-2">
          <summary className="min-h-[44px] cursor-pointer py-2 text-sm font-semibold text-neutral-700">
            {unknown ? `Earlier reports (${history.length})` : `${history.length} report${history.length === 1 ? '' : 's'}`}
          </summary>
          <ul className="space-y-2">
            {history.map((r) => <li key={r.id}><Report r={r} lead="Reported in" /></li>)}
          </ul>
        </details>
      )}

      {mayRecord && unknown && !adding && (
        <button type="button" className={`${secondary} mt-2`} onClick={() => setAdding(true)}>
          Record where they are reported to be
        </button>
      )}
      {adding && <AddForm clientId={clientId} onDone={() => setAdding(false)} />}
    </div>
  );
}

function Report({ r, lead }: { r: WhereaboutsReport; lead: string }) {
  return (
    <div className="mt-1 text-[0.9375rem] text-neutral-800">
      <p>
        {lead} <span className="font-semibold">{r.reported_region}</span>
        <span className="text-neutral-600">
          {' · '}there since {r.there_since ? formatDate(r.there_since) : 'a date not known'}
          {' · '}learned {r.learned_on ? formatDate(r.learned_on) : 'on a date not recorded'}
        </span>
      </p>
      <p className="mt-0.5 text-sm text-neutral-700">{r.how_learned}</p>
      {r.recorded_by && <p className="mt-0.5 text-xs text-neutral-500">Recorded by {r.recorded_by}</p>}
    </div>
  );
}

function AddForm({ clientId, onDone }: { clientId: string; onDone: () => void }) {
  const record = useRecordWhereaboutsReport(clientId);
  const [d, setD] = useState({ reported_region: '', learned_on: '', there_since: '', how_learned: '' });
  const sending = useRef(false);
  const set = (k: keyof typeof d) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setD({ ...d, [k]: e.target.value });
  const ready = d.reported_region.trim().length >= 2 && d.how_learned.trim().length >= 3;
  return (
    <form
      className="mt-2 space-y-2 rounded-lg bg-neutral-50 p-3"
      onSubmit={(ev) => {
        ev.preventDefault();
        if (!ready || sending.current) return;
        sending.current = true;
        const t = (v: string) => (v.trim() === '' ? undefined : v.trim());
        record.mutate({
          reported_region: d.reported_region.trim(), learned_on: t(d.learned_on), there_since: t(d.there_since),
          how_learned: d.how_learned.trim(),
        }, { onSuccess: onDone, onSettled: () => { sending.current = false; } });
      }}
    >
      <p className="text-sm text-neutral-600">A new report never replaces an earlier one; both are kept.</p>
      <label className={label} htmlFor="wr-region">Where they are said to be, as you were told it</label>
      <input id="wr-region" className={field} value={d.reported_region} maxLength={96}
             placeholder="e.g. Florida" onChange={set('reported_region')} />
      <label className={label} htmlFor="wr-learned">The day the agency learned it, if recorded</label>
      <input id="wr-learned" type="date" className={field} value={d.learned_on} onChange={set('learned_on')} />
      <label className={label} htmlFor="wr-since">Since when they are said to be there, if anyone knows</label>
      <input id="wr-since" type="date" className={field} value={d.there_since} onChange={set('there_since')} />
      <label className={label} htmlFor="wr-how">How the agency learned</label>
      <textarea id="wr-how" className={`${field} min-h-[96px] py-2`} value={d.how_learned} maxLength={4000}
                onChange={set('how_learned')} />
      <Failure error={record.error} />
      <div className="flex gap-2">
        <button type="submit" className={primary} disabled={!ready || record.isPending}>Record the report</button>
        <button type="button" className={secondary} onClick={onDone}>Cancel</button>
      </div>
    </form>
  );
}

function Failure({ error }: { error: Error | null }) {
  if (!error) return null;
  const api = error instanceof ApiError ? error : null;
  return (
    <p className="text-sm text-rose-700">
      {error.message}
      {api?.hint && <span className="block">{api.hint}</span>}
    </p>
  );
}
